import { ClWebStreamPreview } from "@chatbotgang/cl-webstream-ui";
import Box from "@mui/material/Box";
import ButtonGroup from "@mui/material/ButtonGroup";
import Stack from "@mui/material/Stack";
import type { SxProps } from "@mui/material/styles";
import type { muiTheme } from "@polifonia/theme";
import { useMemo, useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/Button";
import { Typography } from "@/components/Typography";
import { Trans } from "@/features/i18n/Trans";
import type { InterludeTypes } from "@/interlude";
// Type-only import from the sibling index (namespace merge for `LocaleKey`);
// erased at build time, so it introduces no runtime import cycle.
import type { WebStreamConfigPage } from "@/pages/channels/pages/detail/wccs/WebStreamConfig";
import { HeroAndColorForm } from "@/pages/channels/pages/detail/wccs/WebStreamConfig/HeroAndColorForm";
import { MessagesForm } from "@/pages/channels/pages/detail/wccs/WebStreamConfig/MessagesForm";
import { SizeForm } from "@/pages/channels/pages/detail/wccs/WebStreamConfig/SizeForm";

type WebStreamConfig = InterludeTypes["WebStreamConfig"];

// `label` holds a `<Trans>` element rather than a key string fed to `t()` at the
// call site: a key that only ever reaches `t()` through a variable is invisible to
// every tool that scans for `t("literal")` — i18n-ally shows no inline preview and
// flags nothing missing, and an unused-key sweep reports these as dead and removes
// them. Keeping the keys as literals here is what makes them findable. Module-level
// JSX is safe because `@/features/i18n/Trans` calls `useTranslation()` internally,
// so each mounted instance re-renders on a language change.
const PREVIEW_SCREENS = [
  {
    value: "menu",
    label: <Trans i18nKey="webStreamConfig.preview.menuScreen" />,
  },
  {
    value: "dialogue",
    label: <Trans i18nKey="webStreamConfig.preview.dialogueScreen" />,
  },
  {
    value: "historyEmpty",
    label: <Trans i18nKey="webStreamConfig.preview.historyEmptyScreen" />,
  },
  {
    value: "tokenExpired",
    label: <Trans i18nKey="webStreamConfig.preview.tokenExpiredScreen" />,
  },
] as const satisfies ReadonlyArray<{
  value: React.ComponentProps<typeof ClWebStreamPreview>["screen"];
  label: React.ReactNode;
}>;

const styles = {
  scrollContainer: {
    overflowY: "auto",
    maxHeight: "calc(100vh - 260px)",
  },
  // The layout shell (`layout/MainContent`) wraps every page in a
  // `width: 100%; minWidth: 0; overflow: hidden` Box — page content is expected to
  // adapt to it and must never scroll sideways, because there is no scrollbar to
  // reach what spills. So this row wraps instead of overflowing: the form column
  // gives up width first, and only once it hits the floor set below does the preview
  // drop beneath the form. Without wrapping, the row is an unshrinkable
  // form-width + 32 + 430 and anything that widens it pushes the preview outside the
  // clipped area where it cannot be scrolled to. Wrapping is the fallback, though —
  // see `formContainer` for what keeps it from firing on an ordinary laptop.
  row: {
    flexWrap: "wrap",
  },
  // `flexBasis: 0` rather than the default `auto`, which is what actually decides
  // when the row above wraps. Flex line-breaking compares each item's *hypothetical*
  // main size — content-based under `auto` — so the threshold used to be set by the
  // longest string in the form rather than by anything deliberate: measured in Chrome,
  // adding a ~100-character hint made the row wrap at every container width below
  // ~1440px, which on a MacBook Pro (1512 logical, minus the drawer and padding)
  // dropped the preview below the fold on a screen that cannot be widened.
  //
  // At `0` the form's content no longer votes on the line break, and `minWidth` states
  // the threshold outright: the row wraps under 480 + 32 + 430 = 942px and never above
  // it, whatever the copy does. `min()` keeps that floor from exceeding the container —
  // a hard 480px would overflow a narrower window, which this shell has no horizontal
  // scrollbar to reach, while `0` alone would let the form shrink to a sliver.
  formContainer: {
    flexGrow: 1,
    flexBasis: 0,
    minWidth: "min(480px, 100%)",
  },
  // Pinned to the frame's own 430px, not just `flexShrink: 0`. The screens inside cap
  // themselves at 430, so a narrower column would clip them rather than scale them —
  // wrapping is what gives way instead. But the width also has to be an upper bound:
  // the screen-switcher above the frame is a `fullWidth` ButtonGroup, so without it the
  // column grows to whatever the four labels demand and spills past the clipped shell
  // again (`cimode` renders the raw keys and takes the row to ~1230px). Bounded here,
  // long labels wrap inside their buttons instead of widening the page.
  previewColumn: {
    flexShrink: 0,
    width: "430px",
  },
  // Bounding the column is not enough on its own: a MUI Button's automatic minimum is
  // its nowrap min-content, so four long labels keep their intrinsic width and spill out
  // of the 430px group instead of dividing it. `minWidth: 0` lets them divide it and
  // `whiteSpace: normal` lets the text use a second line rather than being clipped. What
  // makes this worth doing rather than leaving to `cimode`: the same path is reachable
  // from a real translation, and a reviewer lengthening one of these four words would
  // silently clip the row with no scrollbar to reveal it.
  screenButton: {
    minWidth: 0,
    whiteSpace: "normal",
    // `whiteSpace: normal` only breaks at spaces, so a single unbroken token still keeps
    // its full intrinsic width — which is exactly what `cimode` produces, rendering keys
    // like `webStreamConfig.preview.tokenExpiredScreen`. `anywhere` only engages when a
    // word cannot fit on its own line, so ordinary labels break at spaces as before.
    overflowWrap: "anywhere",
  },
  // 430px is the max width MenuView / DialogueShell cap themselves at. The
  // height has to stay a definite value — not `auto`, and not a percentage of an
  // auto-height ancestor: every screen inside the preview sizes itself with
  // `height: 100%`, so an indefinite host turns the dialogue's internal
  // message-list scroll into unbounded page growth and leaves the expired
  // screen top-aligned instead of centered in the frame.
  previewContainer: {
    position: "relative",
    width: "430px",
    height: "640px",
    border: "1px solid",
    borderColor: "divider",
    borderRadius: "8px",
    overflow: "hidden",
  },
} satisfies Record<string, SxProps<typeof muiTheme>>;

namespace ConfigurationTab {
  export interface Props {
    // The form itself lives in the parent (WebStreamConfigDetail) so the tab
    // bar's unsaved-changes guard can read its dirty state and trigger a save.
    onValidSubmit: (config: WebStreamConfig) => void;
    isSaving: boolean;
  }
}

namespace PreviewSection {
  export interface Props {
    selectedLocale: WebStreamConfigPage.LocaleKey;
  }
}

/**
 * The preview column, split from the tab so that watching the form does not re-render the
 * form.
 *
 * `form.watch()` at the tab's root subscribed the whole tab — HeroAndColorForm and every
 * MessagesForm input included — to every field change, so each keystroke re-rendered the
 * inputs being typed into. `useWatch` here subscribes only this column.
 *
 * Watched per top-level field rather than as one `useWatch({ control })`: the no-name form
 * returns a deep-partial of the whole config, which would force optional chaining through
 * `values.menu?.colors` and give back exactly the "a renamed field just reads as absent"
 * hole the field-by-field construction below exists to close.
 */
const PreviewSection: React.FC<PreviewSection.Props> = function PreviewSection({
  selectedLocale,
}) {
  const { t } = useTranslation();
  const { control } = useFormContext<WebStreamConfig>();
  const [previewScreen, setPreviewScreen] =
    useState<(typeof PREVIEW_SCREENS)[number]["value"]>("menu");
  const menu = useWatch({ control, name: "menu" });
  const reentry = useWatch({ control, name: "reentry" });
  const typography = useWatch({ control, name: "typography" });
  const composer = useWatch({ control, name: "composer" });

  // Named field by field rather than handed to the preview whole. Every field of
  // `ClWebStreamPreview.Config` is optional, so passing the entire watch result keeps
  // compiling even after interlude renames one — TypeScript reads the renamed field as
  // merely absent, and absent is allowed. The preview would then silently stop showing
  // that value while the live WebStream still shows it, which is exactly the drift this
  // package exists to prevent. Spelling each field out means a rename resolves to
  // `unknown` through the schema's passthrough index signature and breaks the build here.
  //
  // The guarantee stops at the names spelled out below. `messages` is handed over whole on
  // purpose — the package's `MenuMessages` is a superset of interlude's (it also reads
  // `teamCount`) and the locale record's passthrough carries locales beyond the four, so
  // enumerating message fields would silently drop both. The cost is that renaming a
  // field *inside* a message object is not caught here; today it is caught by
  // `defaultWebStreamConfig satisfies WebStreamConfig` in useWebStreamConfigForm, which
  // is incidental coverage — it would disappear if that literal were ever generated.
  // Memoized per AGENTS.md ("Always use `useMemo` for non-primitive values"), matching
  // WebChatConfig's previewChatConfig. It earns its keep now that this component owns
  // `previewScreen`: switching screens re-renders here while the watched form values are
  // unchanged, so without the memo the preview would receive a fresh config object on a
  // render that changed nothing about the config.
  const previewConfig: ClWebStreamPreview.Config = useMemo(
    () => ({
      typography: {
        baseFontSize: typography?.baseFontSize,
      },
      // A slider moved back to "Default" holds `null` (see SizeForm), which the
      // schema only drops on submit; Vivace reads only `undefined` as unset.
      composer: {
        inputMinHeight: composer?.inputMinHeight ?? undefined,
        buttonSize: composer?.buttonSize ?? undefined,
      },
      menu: {
        heroImageUrl: menu.heroImageUrl,
        bannerScale: menu.bannerScale,
        colors: menu.colors,
        messages: menu.messages,
      },
      reentry: {
        messages: reentry.messages,
      },
    }),
    [menu, reentry, typography, composer],
  );

  return (
    <Stack gap="16px" sx={styles.previewColumn}>
      {/* `fontWeight` prop rather than sx: this Typography is emotion-based and
              takes no sx, same as PageTitle does it. */}
      <Typography variant="h2" fontWeight={700}>
        {t("webStreamConfig.preview.title")}
      </Typography>
      <ButtonGroup fullWidth>
        {PREVIEW_SCREENS.map((screen) => (
          <Button
            key={screen.value}
            fullWidth
            variant={previewScreen === screen.value ? "primary" : "secondary"}
            onClick={() => setPreviewScreen(screen.value)}
            sx={styles.screenButton}
          >
            {screen.label}
          </Button>
        ))}
      </ButtonGroup>
      <Box sx={styles.previewContainer}>
        <ClWebStreamPreview
          screen={previewScreen}
          config={previewConfig}
          locale={selectedLocale}
        />
      </Box>
    </Stack>
  );
};

export const ConfigurationTab: React.FC<ConfigurationTab.Props> =
  function ConfigurationTab({ onValidSubmit, isSaving }) {
    const { t } = useTranslation();
    const form = useFormContext<WebStreamConfig>();
    // Stays here rather than inside PreviewSection: MessagesForm decides which locale is
    // being edited and the preview has to follow it.
    const [selectedLocale, setSelectedLocale] =
      useState<WebStreamConfigPage.LocaleKey>("enUs");

    return (
      <Stack direction="row" gap="32px" sx={styles.row}>
        <Box
          component="form"
          onSubmit={form.handleSubmit(onValidSubmit)}
          sx={styles.formContainer}
        >
          <Stack direction="column" gap="16px">
            <Stack
              direction="row"
              justifyContent="flex-end"
              alignItems="center"
            >
              <Button
                type="submit"
                variant="primary"
                loading={isSaving}
                disabled={isSaving}
              >
                {t("webStreamConfig.saveChanges")}
              </Button>
            </Stack>
            <Box sx={styles.scrollContainer}>
              <HeroAndColorForm />
              <SizeForm />
              <MessagesForm
                selectedLocale={selectedLocale}
                setSelectedLocale={setSelectedLocale}
              />
            </Box>
          </Stack>
        </Box>
        <PreviewSection selectedLocale={selectedLocale} />
      </Stack>
    );
  };
